import { Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { CountryDropdown } from '@/components/ui/CountryDropdown'
import { Field } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { IconButton } from '@/components/ui/IconButton'
import { Input } from '@/components/ui/Input'
import { useFormDraft } from '@/components/ui/useFormDraft'
import type { DirectionTemplate } from '@/types/domain'

/* The direction editor (issue #35 story 1).
 *
 * Its own file rather than a fourth section of SettingsFormSheets.tsx, which is
 * already three sheets and 500 lines: this one is a repeater with per-row
 * validation and a delete confirmation, and it is the sheet a later story will
 * reuse the draft shape of.
 *
 * The form's whole subject is an ORDER. A direction is not a set of places, it
 * is a sequence of them with a normative duration on each transition, so the
 * points are a numbered repeater rather than a multi-select — and every control
 * on a row is labelled with that row's number, because the sheet is unusable
 * with a screen reader if four identical "Point" fields answer to one name.
 */

/** One point as typed.
 *
 *  `days` is a STRING for the reason ThresholdsPanel gives: `Number('')` is 0,
 *  and a numeric state would turn a half-cleared field into a saved zero — here
 *  that means a transition the case advances through the instant it begins. */
export interface DirectionPointDraft {
  /** React's key for this row, and nothing else — never sent, never read by the
   *  payload composer in SettingsPage.
   *
   *  The rows were keyed by ARRAY INDEX, which is stable exactly until somebody
   *  removes a point from the middle of a corridor: every row below it then
   *  inherits the previous occupant's key, and React reconciles the state
   *  INSIDE those rows — the country dropdown's open list, its highlighted
   *  option, the input's selection — onto different data. The controlled values
   *  cap the damage at that, which is precisely what makes it the kind of bug
   *  nobody reproduces on purpose. */
  key: string
  name: string
  /** ISO 3166-1 alpha-2, or `''`. */
  country: string
  /** Days from the previous point, as typed. Ignored on the first row. */
  days: string
}

export interface DirectionDraft {
  name: string
  points: DirectionPointDraft[]
}

/** The ceiling the server enforces (`maxDirectionPoints`), repeated here so the
 *  refusal arrives beside the control rather than as a 422 after a round trip.
 *  Above any real corridor, below anything unbounded. */
const MAX_POINTS = 20

/** The floor. A direction with one point is a place, not a route, and the
 *  server says so as an indexed field problem. */
const MIN_POINTS = 2

/** A monotonic source of row keys for points that have no server id yet.
 *
 *  Module-level rather than per-sheet, so a key minted for one open can never
 *  collide with one minted for the next; `crypto.randomUUID` would do as well
 *  and is a heavier promise than a repeater of at most twenty rows needs. */
let pointSeq = 0

function emptyPoint(): DirectionPointDraft {
  pointSeq += 1
  return { key: `new-${pointSeq}`, name: '', country: '', days: '' }
}

/** A FUNCTION, unlike the constant it replaced: two opens must not share the
 *  row keys of one literal, and the seed below runs once per open anyway. */
function emptyDirection(): DirectionDraft {
  return {
    name: '',
    // Two rows on open, because two is the minimum a direction can be saved
    // with: starting at one would make the first thing the form does after the
    // reader fills it in be a complaint.
    points: [emptyPoint(), emptyPoint()],
  }
}

/** Why a row's day count is refused, as a translation KEY rather than a
 *  sentence — the same split ThresholdsPanel makes, and for the same reason:
 *  `t` is typed against the catalogue and threading it through a helper either
 *  widens the signature to `(key: string) => string`, throwing the compile-time
 *  check away, or drags a type in that nobody else here needs. */
type DayProblem = 'common.form.required' | 'settings.directions.errors.wholeDays'

function dayProblem(raw: string): DayProblem | undefined {
  const trimmed = raw.trim()
  // Required on every point but the first: the server refuses a later point
  // without one, because that is the transition the auto-advance is measured
  // over and a missing number is not a duration.
  if (trimmed === '') return 'common.form.required'
  // Whole days. `parseInt` would silently accept "3.7" as 3.
  if (!/^\d+$/.test(trimmed)) return 'settings.directions.errors.wholeDays'
  return undefined
}

export function DirectionFormSheet({
  open,
  onClose,
  record,
  onSubmit,
  pending,
  onRemove,
  removePending,
}: {
  open: boolean
  onClose: () => void
  record?: DirectionTemplate
  onSubmit: (values: DirectionDraft) => void
  pending?: boolean
  /** Removes this direction. Absent when adding, since there is nothing to
   *  remove — an absent control rather than a disabled one nobody can explain
   *  (invariants: client-ui). */
  onRemove?: () => void
  removePending?: boolean
}) {
  const { t } = useTranslation()
  const [draft, setDraft, touched, markTouched, dirty] = useFormDraft<DirectionDraft>(
    open ? (record?.id ?? 'new') : null,
    () =>
      record
        ? {
            name: record.name,
            points: record.points.map((point) => ({
              // The stored point's own id: stable across every reorder and
              // removal this form can perform, which is what a key is for.
              key: point.id,
              name: point.name,
              country: point.country,
              // The first point's null is not a cleared field, it is the
              // absence of an inbound transition; it never reaches an input.
              days: point.daysFromPrevious === null ? '' : String(point.daysFromPrevious),
            })),
          }
        : emptyDirection(),
  )

  const nameError = touched && !draft.name.trim() ? t('common.form.required') : undefined
  const pointNameError = (point: DirectionPointDraft) =>
    touched && !point.name.trim() ? t('common.form.required') : undefined
  /* Row 0 is exempt from the day check in both directions: it must not carry a
     duration, so its control never accepts one and its value is never read. */
  const pointDayProblem = (point: DirectionPointDraft, index: number) =>
    index === 0 ? undefined : dayProblem(point.days)

  const tooFew = draft.points.length < MIN_POINTS
  const invalid =
    !draft.name.trim() ||
    tooFew ||
    draft.points.some(
      (point, index) => !point.name.trim() || pointDayProblem(point, index) !== undefined,
    )

  const setPoint = (index: number, next: Partial<DirectionPointDraft>) =>
    setDraft({
      ...draft,
      points: draft.points.map((point, i) => (i === index ? { ...point, ...next } : point)),
    })

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={record ? t('settings.directions.edit') : t('settings.directions.new')}
      pending={pending}
      /* The unsaved-changes guard. A direction is the longest repeater on this
         screen — four rows is three fields each — and the sheet is opened by
         clicking a card, which puts a stray press on the scrim one gesture away
         from emptying all of it. */
      dirty={dirty}
      error={touched && invalid ? t('common.form.fixFields') : undefined}
      onSubmit={() => {
        markTouched()
        if (invalid) return
        onSubmit(draft)
      }}
    >
      <Field
        label={t('settings.directions.name')}
        hint={t('settings.directions.nameHint')}
        required
        error={nameError}
      >
        {(props) => (
          <Input
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            {...props}
          />
        )}
      </Field>

      {/* The points, in order.
       *
       * The number disc is not decoration: position is the one fact about a
       * point that is not typed anywhere on its row, and a reader adding a
       * fourth stop to a corridor needs to see where in the sequence it landed.
       *
       * The country control is the flag ALONE and it LEADS the row, exactly as
       * the leg sheet's endpoints do — a country narrows a place, so it is
       * answered first, and a second full-width control repeating
       * "Turkmenistan" would compete with "Ashgabat" for a line that is really
       * one fact. Its name is still spoken (CountryDropdown composes the
       * accessible name). */}
      <Field
        label={t('settings.directions.points')}
        hint={t('settings.directions.pointsHint')}
        required
        error={touched && tooFew ? t('settings.directions.errors.minPoints') : undefined}
      >
        {() => (
          <div className="flex flex-col gap-2">
            {draft.points.map((point, index) => {
              const nameProblem = pointNameError(point)
              const dayKey = pointDayProblem(point, index)
              return (
                <div key={point.key} className="flex items-start gap-1.5">
                  <span
                    aria-hidden
                    className="bg-surface-sunken border-line text-text-muted mt-1.5 flex size-5 shrink-0 items-center justify-center rounded-full border text-[11px] font-medium"
                  >
                    {index + 1}
                  </span>

                  <CountryDropdown
                    flagOnly
                    value={point.country}
                    onChange={(country) => setPoint(index, { country })}
                    aria-label={t('settings.directions.pointCountry', { n: index + 1 })}
                    className="w-auto shrink-0 px-2.5"
                  />

                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <Input
                      value={point.name}
                      aria-label={t('settings.directions.pointName', { n: index + 1 })}
                      placeholder={t('settings.directions.pointPlaceholder')}
                      aria-invalid={nameProblem !== undefined || undefined}
                      onChange={(e) => setPoint(index, { name: e.target.value })}
                    />
                    {nameProblem ? (
                      <span className="text-negative text-[11.5px] leading-snug">
                        {nameProblem}
                      </span>
                    ) : null}
                  </div>

                  {/* The transition INTO this point. The first row has none — it
                      is where the cargo starts — so its control is disabled and
                      reads «старт» rather than being hidden: an absent field on
                      row one and a present one on row two would look like a
                      rendering bug, while a disabled one says the fact does not
                      exist here. */}
                  <div className="flex w-24 shrink-0 flex-col gap-1">
                    <Input
                      // `inputMode` rather than type="number", whose spinners and
                      // locale-dependent decimals this project avoids throughout.
                      inputMode="numeric"
                      disabled={index === 0}
                      value={index === 0 ? t('settings.directions.firstPointStart') : point.days}
                      aria-label={t('settings.directions.pointDays', { n: index + 1 })}
                      placeholder={t('settings.directions.daysPlaceholder')}
                      aria-invalid={touched && dayKey !== undefined ? true : undefined}
                      onChange={(e) => setPoint(index, { days: e.target.value })}
                    />
                    {touched && dayKey ? (
                      <span className="text-negative text-[11.5px] leading-snug">
                        {t(dayKey)}
                      </span>
                    ) : null}
                  </div>

                  <IconButton
                    icon={Trash2}
                    size="sm"
                    className="mt-0.5"
                    label={t('settings.directions.removePoint', { n: index + 1 })}
                    onClick={() =>
                      setDraft({
                        ...draft,
                        points: draft.points.filter((_, i) => i !== index),
                      })
                    }
                  />
                </div>
              )
            })}

            {draft.points.length >= MAX_POINTS ? (
              <span className="text-text-muted text-[12px] leading-snug font-light">
                {t('settings.directions.maxPoints', { max: MAX_POINTS })}
              </span>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                iconLeft={Plus}
                className="self-start"
                onClick={() =>
                  setDraft({ ...draft, points: [...draft.points, emptyPoint()] })
                }
              >
                {t('settings.directions.addPoint')}
              </Button>
            )}
          </div>
        )}
      </Field>

      {/* What the day counts are FOR. Without this the column reads as
          documentation of a journey somebody already knows; it is in fact the
          input that moves a case on its own. */}
      <p className="text-text-muted text-[12px] leading-snug font-light">
        {t('settings.directions.daysNote')}
      </p>

      {onRemove ? (
        <DeleteRow
          onDelete={onRemove}
          pending={pending || removePending}
          resetKey={open ? (record?.id ?? 'new') : null}
          label={t('settings.directions.delete')}
          confirm={t('settings.directions.deleteConfirm')}
          confirmAction={t('settings.directions.deleteConfirmAction')}
        />
      ) : null}
    </FormSheet>
  )
}

/** The dangerous action, below the fields and behind two presses.
 *
 *  A copy of the block `CaseRecordSheets` shares between its three sheets,
 *  which is local to that file and not exported; reaching across features to
 *  import it would make a settings screen depend on the case surface for a
 *  layout primitive. The one part worth carrying over verbatim is the reset:
 *  a half-pressed delete must not survive the sheet closing, or reopening on
 *  another direction puts the dangerous button under a click meant for
 *  something else — and it resets by KEY during render rather than in an
 *  effect, because FormSheet keeps its markup mounted (Base UI `keepMounted`)
 *  so nothing unmounts to clear it.
 *
 *  Confirmed rather than immediate, unlike TemplateFormSheet's delete: that one
 *  argues a confirmation for an act with no consequence teaches people to
 *  dismiss them, and it is right about a template whose words were copied onto
 *  every quote. A direction is copied onto legs the same way, so no running
 *  case moves — but the record itself is a route with up to twenty typed points
 *  and no undo, and re-entering it is minutes rather than a sentence. */
function DeleteRow({
  onDelete,
  pending,
  resetKey,
  label,
  confirm,
  confirmAction,
}: {
  onDelete: () => void
  pending?: boolean
  /** Identifies the record the confirmation belongs to. */
  resetKey: string | null
  label: string
  confirm: string
  confirmAction: string
}) {
  const { t } = useTranslation()
  const [confirming, setConfirming] = useState(false)
  const [confirmedFor, setConfirmedFor] = useState<string | null>(resetKey)

  if (confirmedFor !== resetKey) {
    setConfirmedFor(resetKey)
    setConfirming(false)
  }

  return (
    <div className="border-line-hair mt-1 border-t pt-3">
      {confirming ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-text-body flex-1 text-[12.5px] leading-snug">
            {confirm}
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setConfirming(false)}
            disabled={pending}
          >
            {t('common.actions.cancel')}
          </Button>
          <Button variant="danger" size="sm" onClick={onDelete} disabled={pending}>
            {confirmAction}
          </Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          disabled={pending}
          className="text-negative cursor-pointer text-[12.5px] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {label}
        </button>
      )}
    </div>
  )
}
